// The Ministry's identity, always first (start side) (DESIGN-SYSTEM §9, §00.2). WP-THEME.
// - variant "nav" (the 48 px toolbar, APPLE-SPEC §5.2): the MoET emblem 22 px tall (17 × 22, pre-scaled 1× / 2× / 3× rasters
//   public/brand/moet-emblem-{22,44,66}.png through srcset, so the browser never downsamples the 175 × 228 master) · a 0.5 × 18 px vertical
//   hairline (rgb(0 0 0/.18); dark rgb(255 255 255/.22)) · the ECONIC wordmark at 18 px (a 16 px cap height), 10 px gaps — nothing else. The bilingual ministry name is not in the bar any more: it lives in the footer / About and the
//   classification strip. `ministry="show"` still draws it (12 / 12.5 px, secondary label colour) for surfaces that need it, e.g. About.
// - `brandLine` (nav, Signature and Aurora looks; 21 Oct 2026): the concept deck's header — the emblem, then "UNITED ARAB EMIRATES /
//   MINISTRY OF ECONOMY & TOURISM" in small bronze capitals (Arabic in two lines), then the hairline and the wordmark.
// - variant "horizontal": the MoET horizontal lockup artwork (public/brand/moet-logo-{en,ar}.png), 58 px on the landing head.
// The component draws no link; the shell wraps it (aria-label "Ministry of Economy & Tourism — ECONIC home").
import type { HTMLAttributes } from "react";
import { useEconicLang } from "../theme/primitives";
import { BRAND, BRAND_RATIO, MINISTRY_NAME } from "./names";
import { Wordmark } from "./Wordmark";

export interface MoetLockupProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  variant?: "nav" | "horizontal";
  /** nav only: "hide" (default, §00.2) · "show" · "auto" (shown at ≥ 1280 px; first-pass behaviour). */
  ministry?: "auto" | "show" | "hide";
  /** nav: emblem height (22); horizontal: artwork height (58). */
  height?: number;
  /** nav: wordmark artwork height (18 = a 16 px cap height; the artwork's cap height is 0.87 of its height). */
  wordmarkHeight?: number;
  lang?: "en" | "ar";
  /** nav: the ministry's name in bronze capitals between the emblem and the wordmark (the brand looks). */
  brandLine?: boolean;
}

const BRAND_LINE = {
  en: ["United Arab Emirates", "Ministry of Economy & Tourism"],
  ar: ["الإمارات العربية المتحدة", "وزارة الاقتصاد والسياحة"],
} as const;

export function MoetLockup({ variant = "nav", ministry = "hide", height, wordmarkHeight = 18, lang, brandLine = false, className, ...rest }: MoetLockupProps) {
  const l = useEconicLang(lang);
  if (variant === "horizontal") {
    const h = height ?? 58;
    return (
      <span className={className ? `inline-flex ${className}` : "inline-flex"} {...rest}>
        <img
          src={l === "ar" ? BRAND.lockupAr : BRAND.lockupEn}
          alt={l === "ar" ? MINISTRY_NAME.ar : MINISTRY_NAME.en}
          height={h}
          width={Math.round(h * BRAND_RATIO.lockup)}
          style={{ height: h, width: "auto", display: "block" }}
          draggable={false}
          decoding="async"
        />
      </span>
    );
  }
  const h = height ?? 22;
  const text = ministry === "hide" ? "hidden" : ministry === "auto" ? "flex max-[1279px]:hidden" : "flex";
  // The pre-scaled rasters for the 22 px bar emblem; other heights fall back to the master.
  const raster = h === 22 ? { src: BRAND.emblem22, srcSet: `${BRAND.emblem22} 1x, ${BRAND.emblem44} 2x, ${BRAND.emblem66} 3x` } : { src: BRAND.emblem };
  return (
    <span className={`flex flex-none items-center gap-[10px] ${className ?? ""}`} data-econic-lockup="" {...rest}>
      <img
        {...raster}
        alt=""
        height={h}
        width={h === 22 ? 17 : Math.round(h * BRAND_RATIO.emblem)}
        style={{ height: h, width: "auto", display: "block", flex: "none" }}
        draggable={false}
        decoding="async"
      />
      {ministry !== "hide" && (
        <span className={`${text} flex-col whitespace-nowrap font-medium leading-[1.2] text-label-2`}>
          <span lang="en" className="text-[12px]" style={{ fontFamily: "var(--font-system)" }}>
            {MINISTRY_NAME.en}
          </span>
          <span lang="ar" className="text-[12.5px]" style={{ fontFamily: "var(--font-ar)" }}>
            {MINISTRY_NAME.ar}
          </span>
        </span>
      )}
      {brandLine ? (
        <span className="ec-lk-min" lang={l}>
          <span>{BRAND_LINE[l][0]}</span>
          <span>{BRAND_LINE[l][1]}</span>
        </span>
      ) : null}
      <span aria-hidden className="h-[18px] flex-none bg-[rgb(0_0_0/.18)] dark:bg-[rgb(255_255_255/.22)]" style={{ width: "var(--hairline)" }} />
      <Wordmark height={wordmarkHeight} />
    </span>
  );
}
